import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import { Subscription, distinctUntilChanged } from 'rxjs';
import { AccountantNfce } from 'src/app/models/NfceWizard/Accountant';
import { AlertMessageService } from 'src/app/services/alert-message/alert-message.service';
import { ConfigurationService } from 'src/app/services/configuration/configuration.service';
import { InvalidFieldMessageService } from 'src/app/services/invalid-field-message/invalid-field-message.service';
import { WizardNfceService } from 'src/app/services/nfce/wizard/wizard-nfce.service';
import { blockLetterOnDocumentVerifiers, maskDocument } from 'src/helpers/string-helpers';
import { DocumentValidator } from 'src/validators/document/document.validator';

@Component({
  selector: 'app-accountant',
  templateUrl: './accountant.component.html',
  styleUrls: ['./accountant.component.scss'],
})
export class AccountantComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() isWizard: boolean = true;
  @Output() updateTable = new EventEmitter<void>();

  private _initialAccountant: AccountantNfce | null = null;

  @Input()
  set initialAccountant(value: AccountantNfce | null) {
    this._initialAccountant = value;

    if (value && this.isWizard) {
      this.fillFormFromAccountant(value);
    }
  }

  get initialAccountant(): AccountantNfce | null {
    return this._initialAccountant;
  }

  @ViewChild('phoneInput') phoneInput?: NgxMatIntlTelInputComponent;

  clicked = false;
  textButton: string = this.translateService.instant('wizard.nfce.accountantDetails.save');

  private subscriptions: Subscription[] = [];

  formGroup = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.minLength(3)]),
    email: new FormControl('', [Validators.required, Validators.email]),
    document: new FormControl('', [DocumentValidator.isValid, Validators.maxLength(18)]),
    phone: new FormControl(''),
    dialCode: new FormControl(''),
  });

  constructor(
    private messageService: InvalidFieldMessageService,
    private wizardNfceService: WizardNfceService,
    private configuration: ConfigurationService,
    private alertService: AlertMessageService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.initValueChanges();
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      this.formGroup.controls.phone.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private initValueChanges(): void {
    this.subscriptions.push(
      this.watchControl('name'),
      this.watchControl('email'),
      this.watchControl('phone'),
      this.watchControl('document', maskDocument)
    );
  }

  private watchControl(
    controlName: 'name' | 'email' | 'document' | 'phone',
    maskFn?: (value: string) => string
  ): Subscription {
    const control = this.formGroup.controls[controlName];

    return control.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(value => {
        if (value === null || value === undefined) return;

        const currentValue = String(value);

        if (maskFn && currentValue) {
          const masked = maskFn(currentValue);
          if (masked !== currentValue) {
            control.setValue(masked, { emitEvent: false });
          }
        }

        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      });
  }

  private fillFormFromAccountant(accountant: AccountantNfce): void {
    const phoneFormatted =
      accountant.phone && accountant.dialCode
        ? this.formatPhone(accountant.dialCode, accountant.phone)
        : null;

    this.formGroup.patchValue(
      {
        name: accountant.name ?? '',
        email: accountant.email ?? '',
        document: accountant.document ?? '',
        phone: phoneFormatted,
        dialCode: accountant.dialCode ?? '',
      },
      { emitEvent: false }
    );

    this.formGroup.controls.phone.updateValueAndValidity({ emitEvent: false });

    queueMicrotask(() => {
      this.phoneInput?.writeValue(phoneFormatted);
    });
  }

  clearForm() {
    this.phoneInput?.reset();
    this.formGroup.controls.document.enable({ emitEvent: false });
    this.formGroup.reset({}, { emitEvent: false });
  }

  dialCodeChange(dialCode: string) {
    this.formGroup.controls.dialCode.setValue(dialCode, { emitEvent: false });
    this.formGroup.controls.phone.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    if (target?.getAttribute('formcontrolname') !== 'document') return;
    blockLetterOnDocumentVerifiers(event, this.formGroup.controls.document.value);
  }

  get messageError() {
    return this.messageService;
  }

  async clickSave() {
    if (!this.isValid()) return false;
    await this.sendAccountant();
    return true;
  }

  private isValid(): boolean {
    this.formGroup.markAllAsTouched();

    Object.values(this.formGroup.controls).forEach(control => {
      control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });

    const phoneValid = this.isPhoneValid();
    this.formGroup.updateValueAndValidity({ emitEvent: false });

    return this.formGroup.valid && phoneValid;
  }

  private isPhoneValid(): boolean {
    const control = this.formGroup.get('phone');

    if (!this.phoneInput) {
      control?.setErrors(null);
      return true;
    }

    const phone = this.phoneInput.phoneNumber;
    const dialCode = this.formGroup.controls.dialCode.value;

    if (!phone && !dialCode) {
      control?.setErrors(null);
      return true;
    }

    if (!phone && dialCode) {
      control?.setErrors(null);
      return true;
    }

    if (phone && !dialCode) {
      control?.setErrors({ invalidPhone: true });
      return false;
    }

    const isValid = this.phoneInput.numberInstance?.isValid() ?? false;
    control?.setErrors(isValid ? null : { invalidPhone: true });

    return isValid;
  }

  private formatPhone(dialCode: string, phone: string): string {
    return `+${dialCode}${phone}`;
  }

  private async sendAccountant() {
    const saved = await this.wizardNfceService.setAccountantDetails(this.objectCreatedToSend());

    if (saved) {
      this.clicked = true;
      this.textButton = this.translateService.instant('wizard.nfce.accountantDetails.saved');

      if (!this.isWizard) {
        this.alertService.alertSuccessMessage(
          this.translateService.instant('message.accountantCreatedSuccess')
        );
        this.clearForm();
        this.updateTable.emit();
      }
    }
  }

  private objectCreatedToSend(): AccountantNfce {
    const accountant = this.formGroup.getRawValue();

    let phone: string | null = null;
    let dialCode: string | null = null;

    if (accountant.phone) {
      phone = this.configuration.returnOnlyPhoneNumberWithoutDialCode(
        accountant.dialCode!,
        accountant.phone
      );
      dialCode = accountant.dialCode;
    }

    return {
      name: accountant.name!,
      email: accountant.email!,
      document: accountant.document,
      phone,
      dialCode,
    } as AccountantNfce;
  }
}